import cx from 'classnames';
import PropTypes from 'prop-types';
import uniqid from 'uniqid';
import { useState } from 'react';
import { ArrowLink, Link, RadioButton } from '@bwoty-web/ui-kit';
import OnRequestPopup from './OnRequestPopup';

const RoomGroupRow = ({ selectedRoomGroup, roomGroup, labels, keyIndex, selectRoomGroup, showRoomTypeLink }) => {
  const [isOnRequestPopupOpen, setIsOnRequestPopupOpen] = useState(false);

  const selectRoomGroupFunc = () => {
    if (roomGroup === selectedRoomGroup) {
      return;
    }
    selectRoomGroup(roomGroup);
  };

  const clickOnRequestLink = () => {
    setIsOnRequestPopupOpen(!isOnRequestPopupOpen);
  };

  const getBedTypeDescription = ({ description, type }) => {
    const bedTypeDescription = labels[type.toLowerCase()];
    if (bedTypeDescription) return bedTypeDescription;
    return description;
  };

  function sentenceCase(str) {
    const lower = str.toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  }

  function getBedTypesAsAmenities(bedTypesList) {
    if (!Array.isArray(bedTypesList)) return [];

    return bedTypesList.map((bedType, index) => ({
      id: `${index}-${bedType.description}`,
      name: `${bedType.quantity} ${getBedTypeDescription(bedType)}`,
    }));
  }

  const renderRoomQuantity = (quantity) => {
    return (
      <span
        className="price-matrix-table__room-quantity"
        key={uniqid('room-quantity-')}
      >
        ({quantity} {labels.numberOfSuffix})
      </span>
    );
  };

  const ariaDescriptionId = `price-matrix-aria-description-${keyIndex}`;

  const renderRoomList = () => {
    let showOnRequestLabel = false;

    const openRoomModal = (roomId) => {
      if (roomId && typeof window !== 'undefined' && window) {
        window.postMessage(`cmd.open.popup:${roomId}`);
      }
    };

    const roomId = roomGroup?.roomType?.wvId || roomGroup?.roomType?.externalId;

    const roomList = roomGroup.rooms.map((room) => {
      const roomText = (
        <div
          key={uniqid('room-list-')}
          className="price-matrix-table__room-description"
        >
          {roomId && showRoomTypeLink ? (
            <Link onClick={() => openRoomModal(roomId)}>{room.description}</Link>
          ) : (
            room.description
          )}
          {renderRoomQuantity(room.quantity)}
        </div>
      );

      if (!room.isAvailable && !showOnRequestLabel) showOnRequestLabel = true;
      return roomText;
    });

    const bedTypesList = getBedTypesAsAmenities(roomGroup?.bedTypes);
    const amenities = roomGroup?.amenities || [];
    const finalAmenities = [...bedTypesList, ...amenities];

    return (
      <>
        {roomList}
        {showOnRequestLabel && (
          <ArrowLink
            className="price-matrix-table__on-request"
            onClick={clickOnRequestLink}
          >
            {labels.availableOnRequest}
          </ArrowLink>
        )}

        {finalAmenities.length > 0 && (
          <ul className="price-matrix-table__amenities">
            {finalAmenities.map((amenity) => {
              if (!amenity.name) return undefined;
              const formattedAmenity = sentenceCase(amenity.name);
              return <li key={amenity.id}>{formattedAmenity}</li>;
            })}
          </ul>
        )}

        {roomGroup.hasCharges && <div className="price-matrix-table__non-refundable">{labels.nonRefundableLabel}</div>}
      </>
    );
  };

  const { totalPrice, isDiscounted } = roomGroup.priceBreakDowns;
  const isSelectedGroup = selectedRoomGroup.bookingEngineRoomGroupKey === roomGroup.bookingEngineRoomGroupKey;

  const roomGroupClasses = cx('price-matrix-table__row', {
    'price-matrix-table__row--selected': isSelectedGroup,
    'price-matrix-table__row--first': keyIndex === 0,
  });

  const totalPriceClasses = cx('price-matrix-table__total-price', {
    'price-matrix-table__total-price--has-discount': isDiscounted,
  });

  const chooseText = isSelectedGroup ? labels.roomSelectedBtnLabel : labels.ChooseRoomType;
  const priceInfo = `${labels.totalPrice} ${totalPrice}`;
  const ariaLabel = `${chooseText}: ${priceInfo}.`;

  return (
    <div className={roomGroupClasses}>
      <div className="price-matrix-table__left-column">
        <RadioButton
          checked={isSelectedGroup}
          renderAs="div"
        />
      </div>
      <div
        className="price-matrix-table__middle-column"
        id={ariaDescriptionId}
      >
        {renderRoomList()}
      </div>
      <button
        onClick={selectRoomGroupFunc}
        type="button"
        aria-label={ariaLabel}
        aria-describedby={ariaDescriptionId}
        className={totalPriceClasses}
      >
        {totalPrice}
      </button>

      <OnRequestPopup
        labels={labels}
        isOpen={isOnRequestPopupOpen}
        hidePopup={() => setIsOnRequestPopupOpen(false)}
      />
    </div>
  );
};

RoomGroupRow.propTypes = {
  roomGroup: PropTypes.shape({
    roomType: PropTypes.shape({
      wvId: PropTypes.number,
      externalId: PropTypes.string,
    }),
    rooms: PropTypes.arrayOf(PropTypes.shape({})),
    bedTypes: PropTypes.arrayOf(PropTypes.shape({})),
    amenities: PropTypes.arrayOf(PropTypes.shape({})),
    hasCharges: PropTypes.bool,
    specialOffer: PropTypes.shape({}),
    priceBreakDowns: PropTypes.shape({
      totalPrice: PropTypes.string,
      totalDiscount: PropTypes.string,
      isDiscounted: PropTypes.bool,
    }),
    bookingEngineRoomGroupKey: PropTypes.string,
  }).isRequired,
  labels: PropTypes.shape({
    kingbed: PropTypes.string.isRequired,
    queenbed: PropTypes.string.isRequired,
    fullbed: PropTypes.string.isRequired,
    twinbed: PropTypes.string.isRequired,
    sofabed: PropTypes.string.isRequired,
    bunkbed: PropTypes.string.isRequired,
    availableOnRequest: PropTypes.string.isRequired,
    nonRefundableLabel: PropTypes.string.isRequired,
    numberOfSuffix: PropTypes.string.isRequired,
    roomSelectedBtnLabel: PropTypes.string.isRequired,
    ChooseRoomType: PropTypes.string.isRequired,
    totalPrice: PropTypes.string.isRequired,
  }).isRequired,
  selectRoomGroup: PropTypes.func.isRequired,
  selectedRoomGroup: PropTypes.shape({
    bookingEngineRoomGroupKey: PropTypes.string,
  }).isRequired,
  keyIndex: PropTypes.number.isRequired,
  showRoomTypeLink: PropTypes.bool.isRequired,
};

export default RoomGroupRow;
